import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { GLYPHS } from '@/components/ui';

interface DataComparisonProps {
  oldData: any;
  currentData: any;
}

const DataComparison: React.FC<DataComparisonProps> = ({ 
  oldData, 
  currentData
}) => {
  const fields = [
    { key: 'name', label: '항목명', type: 'text' },
    { key: 'money', label: '금액', type: 'money' },
    { key: 'content', label: '내용', type: 'text' }
  ];

  // 변경된 필드만 필터링
  const changedFields = fields.filter(field => {
    const oldValue = oldData?.[field.key];
    const currentValue = currentData?.[field.key];
    return oldValue !== currentValue;
  });

  if (changedFields.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 3,
        color: 'var(--fg-tertiary)'
      }}>
        <Typography variant="body2">
          변경된 내용이 없습니다.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 2,
          background: 'var(--bg-base)',
          border: '1px solid var(--border-subtle)'
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: 'var(--bg-elev-1)' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                필드
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                현재 값
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', textAlign: 'center' }}>
                변경
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                이전 값
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changedFields.map(field => {
              const oldValue = oldData?.[field.key];
              const currentValue = currentData?.[field.key];
              
              return (
                <TableRow key={field.key} sx={{ '&:hover': { background: 'var(--bg-elev-1)' } }}>
                  <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    {field.label}
                  </TableCell>
                  <TableCell>
                    <ValueCell 
                      value={currentValue} 
                      type={field.type} 
                      variant="current"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', color: 'var(--fg-tertiary)' }}>
                    {GLYPHS.arrow}
                  </TableCell>
                  <TableCell>
                    <ValueCell 
                      value={oldValue} 
                      type={field.type} 
                      variant="old"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// 값 셀 컴포넌트
const ValueCell: React.FC<{ 
  value: any; 
  type: string; 
  variant: 'current' | 'old' 
}> = ({ value, type, variant }) => {
  const formattedValue = formatValue(value, type);
  const isEmpty = !value && value !== 0;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1,
      minHeight: 32
    }}>
      {isEmpty ? (
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'var(--fg-disabled)',
            fontStyle: 'italic',
            fontSize: '0.8rem'
          }}
        >
          -
        </Typography>
      ) : (
        <>
          {variant === 'current' && (
            <Chip
              label="현재"
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                fontWeight: 500,
                borderColor: 'var(--c-red-600)',
                color: 'var(--c-red-600)',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          )}
          {variant === 'old' && (
            <Chip
              label="이전"
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                fontWeight: 500,
                borderColor: 'var(--primary)',
                color: 'var(--primary)',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          )}
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.8rem',
              fontWeight: variant === 'current' ? 600 : 400,
              color: variant === 'current' ? 'var(--fg-strong)' : 'var(--fg-tertiary)'
            }}
          >
            {formattedValue}
          </Typography>
        </>
      )}
    </Box>
  );
};

// 값 포맷팅 함수
const formatValue = (value: any, type: string) => {
  if (value === null || value === undefined) return '-';
  
  switch (type) {
    case 'money':
      return new Intl.NumberFormat('ko-KR').format(value);
    case 'text':
      return String(value);
    default:
      return String(value);
  }
};

export default DataComparison;
